import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EventIcon from '@mui/icons-material/Event';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TimelineIcon from '@mui/icons-material/Timeline';
import GridViewIcon from '@mui/icons-material/GridView';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PlaceIcon from '@mui/icons-material/Place';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { CmsPastProject } from '../../types/cms';
import { WebsiteCmsService } from '../../services/websiteCmsService';

interface PastProjectsManagerTabProps {
  pastProjects: CmsPastProject[];
  onSave: (item: CmsPastProject) => void;
  onDelete: (id: string) => void;
}

export default function PastProjectsManagerTab({
  pastProjects,
  onSave,
  onDelete,
}: PastProjectsManagerTabProps) {
  const [editingItem, setEditingItem] = useState<CmsPastProject | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [viewMode, setViewMode] = useState<'timeline' | 'grid'>('timeline');
  const [selectedEra, setSelectedEra] = useState<string>('all');

  // Sort chronologically ascending
  const sortedProjects = [...pastProjects].sort((a, b) => (a.sortYear || 1989) - (b.sortYear || 1989));

  // Filter by Era
  const filteredProjects = sortedProjects.filter((p) => {
    const yr = p.sortYear || 1989;
    if (selectedEra === '1988-1995') return yr >= 1988 && yr <= 1995;
    if (selectedEra === '1996-2005') return yr >= 1996 && yr <= 2005;
    if (selectedEra === '2006-2019') return yr >= 2006 && yr <= 2019;
    if (selectedEra === '2020-present') return yr >= 2020;
    return true;
  });

  // Calculate Era Stats
  const totalUnits = pastProjects.reduce((sum, p) => sum + (Number(p.totalUnits) || 0), 0);
  const totalSqft = pastProjects.reduce((sum, p) => {
    const units = Number(p.totalUnits) || 0;
    const area = Number(p.superBuiltUpArea) || 1050;
    return sum + units * area;
  }, 0);

  const handleOpenAdd = () => {
    setEditingItem({
      id: `past-${Date.now()}`,
      entryType: 'project',
      name: '',
      yearRange: '2024',
      sortYear: new Date().getFullYear(),
      projectType: 'Residential Apartments',
      location: 'Surat',
      totalUnits: 0,
      superBuiltUpArea: 1100,
      description: '',
      coverImageUrl: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item: CmsPastProject) => {
    setEditingItem({ ...item });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingItem) return;

    setUploadingCover(true);
    try {
      const url = await WebsiteCmsService.uploadMedia(file, 'past-projects', 'covers');
      setEditingItem((prev) => (prev ? { ...prev, coverImageUrl: url } : null));
    } catch (err) {
      console.error('Image upload failed', err);
    } finally {
      setUploadingCover(false);
      e.target.value = '';
    }
  };

  const handleSave = () => {
    if (!editingItem || !editingItem.name) return;
    onSave(editingItem);
    setDialogOpen(false);
    setEditingItem(null);
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Top Header & Summary Metric Banner */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 2.5 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
              🏛️ Heritage & Past Projects Track Record (1988 – Present)
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Chronological legacy timeline of delivered residential landmarks, townships, and historical foundation milestones.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(_, val) => val && setViewMode(val)}
              sx={{ bgcolor: '#fafafa' }}
            >
              <ToggleButton value="timeline" sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontWeight: 600 }}>
                <TimelineIcon fontSize="small" sx={{ mr: 0.5 }} /> Timeline Journey
              </ToggleButton>
              <ToggleButton value="grid" sx={{ px: 1.5, py: 0.5, textTransform: 'none', fontWeight: 600 }}>
                <GridViewIcon fontSize="small" sx={{ mr: 0.5 }} /> Grid View
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
              sx={{ bgcolor: '#9d6f32', '&:hover': { bgcolor: '#805926' }, textTransform: 'none', fontWeight: 700 }}
            >
              Add Project / Milestone
            </Button>
          </Stack>
        </Stack>

        {/* Year-Wise Progress Rail & Legacy Highlights */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: '#faf6f0',
            border: '1px solid #e8decb',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>
              Historical Span
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#9d6f32' }}>
              37+ Years (1988 – 2026)
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>
              Delivered Schemes
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
              {pastProjects.filter((p) => p.entryType === 'project').length} Landmark Schemes
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>
              Total Units Delivered
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
              {totalUnits.toLocaleString('en-IN')}+ Homes
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>
              Delivered Area
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#2e7d32' }}>
              {(totalSqft / 100000).toFixed(2)} Lakh Sq.Ft
            </Typography>
          </Box>
        </Box>

        {/* Era Filters / Timeline Navigation Bar */}
        <Stack direction="row" spacing={1} sx={{ mt: 2.5, overflowX: 'auto', pb: 0.5 }}>
          {[
            { id: 'all', label: 'All Eras (1988–Present)', count: pastProjects.length },
            { id: '1988-1995', label: '1988–1995: Foundation Era', count: sortedProjects.filter((p) => (p.sortYear || 0) <= 1995).length },
            { id: '1996-2005', label: '1996–2005: Expansion & Bungalows', count: sortedProjects.filter((p) => (p.sortYear || 0) >= 1996 && (p.sortYear || 0) <= 2005).length },
            { id: '2006-2019', label: '2006–2019: Mega Townships', count: sortedProjects.filter((p) => (p.sortYear || 0) >= 2006 && (p.sortYear || 0) <= 2019).length },
            { id: '2020-present', label: '2020–Present: Swarna Group Era', count: sortedProjects.filter((p) => (p.sortYear || 0) >= 2020).length },
          ].map((era) => (
            <Chip
              key={era.id}
              label={`${era.label} (${era.count})`}
              clickable
              onClick={() => setSelectedEra(era.id)}
              sx={{
                fontWeight: 700,
                fontSize: '0.8rem',
                bgcolor: selectedEra === era.id ? '#9d6f32' : '#f0f0f0',
                color: selectedEra === era.id ? '#ffffff' : '#444',
                '&:hover': { bgcolor: selectedEra === era.id ? '#805926' : '#e4e4e4' },
              }}
            />
          ))}
        </Stack>
      </Paper>

      {/* TIMELINE JOURNEY VIEW */}
      {viewMode === 'timeline' ? (
        <Box sx={{ position: 'relative', px: { xs: 1, md: 3 }, py: 2 }}>
          {/* Central Connecting Progress Line */}
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              bottom: 20,
              left: { xs: 30, md: 50 },
              width: 3,
              bgcolor: '#d5b991',
              borderRadius: 2,
            }}
          />

          <Stack spacing={3.5}>
            {filteredProjects.map((p, idx) => {
              const isEvent = p.entryType === 'event';
              return (
                <Box
                  key={p.id}
                  sx={{
                    position: 'relative',
                    pl: { xs: 7, md: 10 },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Timeline Year Node Dot */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: { xs: 16, md: 36 },
                      top: 18,
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: isEvent ? '#1a1a1a' : '#9d6f32',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 0 4px #ffffff, 0 0 0 6px #d5b991',
                      zIndex: 2,
                    }}
                  >
                    {isEvent ? <EmojiEventsIcon sx={{ fontSize: 16, color: '#e5b36b' }} /> : <ApartmentIcon sx={{ fontSize: 16 }} />}
                  </Box>

                  {/* Card Container */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      border: isEvent ? '2px solid #9d6f32' : '1px solid #e0e0e0',
                      bgcolor: isEvent ? '#231d17' : '#ffffff',
                      color: isEvent ? '#ffffff' : '#1a1a1a',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                        borderColor: '#9d6f32',
                      },
                    }}
                  >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'center' }}>
                      {/* Left: Image or Icon */}
                      {p.coverImageUrl ? (
                        <Box
                          component="img"
                          src={p.coverImageUrl}
                          alt={p.name}
                          sx={{
                            width: { xs: '100%', sm: 140 },
                            height: 100,
                            objectFit: 'cover',
                            borderRadius: 2,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: { xs: '100%', sm: 120 },
                            height: 90,
                            borderRadius: 2,
                            bgcolor: isEvent ? '#3a3024' : '#f5f0e8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {isEvent ? <EmojiEventsIcon sx={{ fontSize: 40, color: '#e5b36b' }} /> : <ApartmentIcon sx={{ fontSize: 40, color: '#9d6f32' }} />}
                        </Box>
                      )}

                      {/* Middle: Details */}
                      <Box sx={{ flexGrow: 1 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.8, flexWrap: 'wrap' }}>
                          <Chip
                            label={p.yearRange}
                            size="small"
                            sx={{
                              bgcolor: isEvent ? '#e5b36b' : '#9d6f32',
                              color: isEvent ? '#1a1a1a' : '#ffffff',
                              fontWeight: 800,
                              fontSize: '0.75rem',
                            }}
                          />
                          {isEvent ? (
                            <Chip
                              label="★ Historical Milestone"
                              size="small"
                              sx={{ bgcolor: 'rgba(229,179,107,0.15)', color: '#e5b36b', fontWeight: 700 }}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ color: isEvent ? '#aaa' : '#666', fontWeight: 600 }}>
                              {p.projectType || 'Residential'} • {p.location || 'Surat'}
                            </Typography>
                          )}
                        </Stack>

                        <Typography variant="h6" sx={{ fontWeight: 800, color: isEvent ? '#ffffff' : '#1a1a1a', fontSize: '1.05rem' }}>
                          {p.name}
                        </Typography>

                        {isEvent ? (
                          <Typography variant="body2" sx={{ color: '#d0c5b4', mt: 0.5, fontStyle: 'italic', lineHeight: 1.4 }}>
                            "{p.description}"
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                            {p.totalUnits ? (
                              <Chip
                                label={`${p.totalUnits} Units`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem', borderColor: '#ccc', color: isEvent ? '#fff' : '#333' }}
                              />
                            ) : null}
                            {p.superBuiltUpArea ? (
                              <Chip
                                label={`${p.superBuiltUpArea.toLocaleString('en-IN')} sq.ft/unit`}
                                size="small"
                                sx={{ bgcolor: '#f4ece1', color: '#9d6f32', fontWeight: 600, fontSize: '0.75rem' }}
                              />
                            ) : null}
                            {p.totalUnits && p.superBuiltUpArea ? (
                              <Chip
                                label={`${((p.totalUnits * p.superBuiltUpArea) / 1000).toFixed(0)}k sq.ft Total Area`}
                                size="small"
                                sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.75rem' }}
                              />
                            ) : null}
                          </Stack>
                        )}
                      </Box>

                      {/* Right: Actions */}
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <IconButton size="small" onClick={() => handleOpenEdit(p)} sx={{ color: isEvent ? '#90caf9' : '#1976d2', bgcolor: isEvent ? 'rgba(255,255,255,0.05)' : '#f0f7ff' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(p.id)} sx={{ color: '#e57373', bgcolor: isEvent ? 'rgba(255,255,255,0.05)' : '#fff5f5' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : (
        /* GRID VIEW */
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          {filteredProjects.map((p) => (
            <Card key={p.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2.5, border: '1px solid #e0e0e0' }}>
              <Box sx={{ position: 'relative', height: 140, bgcolor: p.entryType === 'event' ? '#2c251c' : '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {p.coverImageUrl ? (
                  <img
                    src={p.coverImageUrl}
                    alt={p.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : p.entryType === 'event' ? (
                  <EventIcon sx={{ fontSize: 48, color: '#e5b36b' }} />
                ) : (
                  <ApartmentIcon sx={{ fontSize: 48, color: '#9d6f32' }} />
                )}
                <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                  <Chip
                    label={p.yearRange}
                    size="small"
                    sx={{ bgcolor: '#9d6f32', color: '#fff', fontWeight: 700 }}
                  />
                </Box>
              </Box>

              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }}>
                  {p.name}
                </Typography>
                {p.entryType === 'project' ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {p.projectType || 'Residential'} • {p.location || 'Surat'}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                      {p.totalUnits ? (
                        <Chip label={`${p.totalUnits} Units`} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                      ) : null}
                      {p.superBuiltUpArea ? (
                        <Chip
                          label={`${p.superBuiltUpArea.toLocaleString('en-IN')} sq.ft/unit`}
                          size="small"
                          sx={{ bgcolor: '#f4ece1', color: '#9d6f32', fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      ) : null}
                      {p.totalUnits && p.superBuiltUpArea ? (
                        <Chip
                          label={`${((p.totalUnits * p.superBuiltUpArea) / 1000).toFixed(0)}k sq.ft Total`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#555', mt: 0.5, fontStyle: 'italic', fontSize: '0.85rem' }}>
                    {p.description}
                  </Typography>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <IconButton size="small" onClick={() => handleOpenEdit(p)} sx={{ color: '#1976d2' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(p.id)} sx={{ color: '#d32f2f' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingItem?.id && pastProjects.some((p) => p.id === editingItem.id) ? 'Edit Past Project' : 'Add Past Project / Milestone'}
        </DialogTitle>
        <DialogContent dividers>
          {editingItem && (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Entry Type</InputLabel>
                <Select
                  value={editingItem.entryType}
                  label="Entry Type"
                  onChange={(e) => setEditingItem({ ...editingItem, entryType: e.target.value as any })}
                >
                  <MenuItem value="project">Delivered Project</MenuItem>
                  <MenuItem value="event">Historical Milestone Event</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Name / Title"
                fullWidth
                value={editingItem.name}
                onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                required
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  label="Year Range Display (e.g. 1989-90)"
                  fullWidth
                  value={editingItem.yearRange}
                  onChange={(e) => setEditingItem({ ...editingItem, yearRange: e.target.value })}
                />
                <TextField
                  label="Sort Year (Numeric, e.g. 1989)"
                  type="number"
                  fullWidth
                  value={editingItem.sortYear}
                  onChange={(e) => setEditingItem({ ...editingItem, sortYear: Number(e.target.value) })}
                />
              </Box>

              {editingItem.entryType === 'project' ? (
                <>
                  <TextField
                    label="Project Type"
                    fullWidth
                    placeholder="e.g. Residential Apartments / Row Houses / Mixed Use"
                    value={editingItem.projectType || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, projectType: e.target.value })}
                  />
                  <TextField
                    label="Location"
                    fullWidth
                    placeholder="e.g. Adajan, Surat"
                    value={editingItem.location || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, location: e.target.value })}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      label="Total Units Delivered"
                      type="number"
                      fullWidth
                      value={editingItem.totalUnits || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, totalUnits: Number(e.target.value) })}
                    />
                    <TextField
                      label="Super Built-Up Area (Sq.Ft / Unit)"
                      type="number"
                      fullWidth
                      placeholder="e.g. 1000"
                      value={editingItem.superBuiltUpArea || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, superBuiltUpArea: Number(e.target.value) })}
                      helperText={
                        editingItem.totalUnits && editingItem.superBuiltUpArea
                          ? `Total Area: ${(editingItem.totalUnits * editingItem.superBuiltUpArea).toLocaleString('en-IN')} Sq.Ft`
                          : undefined
                      }
                    />
                  </Box>
                  {/* Cover Image Uploader */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', mb: 1, display: 'block', textTransform: 'uppercase' }}>
                      Project Cover Image
                    </Typography>
                    {editingItem.coverImageUrl ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          bgcolor: '#fafafa',
                        }}
                      >
                        <Box
                          component="img"
                          src={editingItem.coverImageUrl}
                          alt="Cover Preview"
                          sx={{
                            width: 100,
                            height: 70,
                            objectFit: 'cover',
                            borderRadius: 1.5,
                            border: '1px solid #e0e0e0',
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                            ✓ Image Attached
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Button
                              component="label"
                              size="small"
                              variant="outlined"
                              startIcon={uploadingCover ? <CircularProgress size={14} /> : <PhotoCameraIcon />}
                              disabled={uploadingCover}
                              sx={{ textTransform: 'none', borderColor: '#9d6f32', color: '#9d6f32' }}
                            >
                              Replace Image
                              <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="text"
                              onClick={() => setEditingItem({ ...editingItem, coverImageUrl: '' })}
                              sx={{ textTransform: 'none' }}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </Box>
                      </Paper>
                    ) : (
                      <Paper
                        component="label"
                        variant="outlined"
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          borderStyle: 'dashed',
                          borderColor: '#9d6f32',
                          bgcolor: '#fdfbf9',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f7f1e7' },
                        }}
                      >
                        <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                        {uploadingCover ? (
                          <Stack alignItems="center" spacing={1}>
                            <CircularProgress size={28} sx={{ color: '#9d6f32' }} />
                            <Typography variant="body2" sx={{ color: '#9d6f32', fontWeight: 600 }}>
                              Uploading to Firebase Storage...
                            </Typography>
                          </Stack>
                        ) : (
                          <>
                            <CloudUploadIcon sx={{ fontSize: 36, color: '#9d6f32', mb: 1 }} />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#9d6f32' }}>
                              Click to Upload Cover Image
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>
                              PNG, JPG, WebP supported • Automatically saved to Firebase Storage
                            </Typography>
                          </>
                        )}
                      </Paper>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <TextField
                    label="Event Story / Milestone Description"
                    fullWidth
                    multiline
                    rows={3}
                    value={editingItem.description || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                  />
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', mb: 1, display: 'block', textTransform: 'uppercase' }}>
                      Milestone Event Photo (Optional)
                    </Typography>
                    {editingItem.coverImageUrl ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          bgcolor: '#fafafa',
                        }}
                      >
                        <Box
                          component="img"
                          src={editingItem.coverImageUrl}
                          alt="Event Preview"
                          sx={{
                            width: 100,
                            height: 70,
                            objectFit: 'cover',
                            borderRadius: 1.5,
                            border: '1px solid #e0e0e0',
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                            ✓ Event Photo Attached
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Button
                              component="label"
                              size="small"
                              variant="outlined"
                              startIcon={uploadingCover ? <CircularProgress size={14} /> : <PhotoCameraIcon />}
                              disabled={uploadingCover}
                              sx={{ textTransform: 'none', borderColor: '#9d6f32', color: '#9d6f32' }}
                            >
                              Replace Image
                              <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="text"
                              onClick={() => setEditingItem({ ...editingItem, coverImageUrl: '' })}
                              sx={{ textTransform: 'none' }}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </Box>
                      </Paper>
                    ) : (
                      <Paper
                        component="label"
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          borderStyle: 'dashed',
                          borderColor: '#bbb',
                          bgcolor: '#fafafa',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f5f5f5', borderColor: '#9d6f32' },
                        }}
                      >
                        <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                        {uploadingCover ? (
                          <Stack alignItems="center" spacing={1}>
                            <CircularProgress size={24} sx={{ color: '#9d6f32' }} />
                            <Typography variant="body2" sx={{ color: '#9d6f32', fontWeight: 600 }}>
                              Uploading...
                            </Typography>
                          </Stack>
                        ) : (
                          <>
                            <CloudUploadIcon sx={{ fontSize: 28, color: '#888', mb: 0.5 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>
                              Click to Upload Event Photo
                            </Typography>
                          </>
                        )}
                      </Paper>
                    )}
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#9d6f32', '&:hover': { bgcolor: '#805926' } }}>
            Save Entry
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
